import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../services/api";
import styles from "./WelcomeCallsPage.module.css";
import Card from "../../components/common/Card.jsx";
import Button from "../../components/common/Button.jsx";
import Input from "../../components/common/Input.jsx";
import Modal from "../../components/common/Modal.jsx";
import ListLoadError from "../../components/admin/ListLoadError";
import AdminFiltersModal, {
  FiltersButton,
  FilterChips,
  buildFilterChips,
  countActiveFilters,
  removeFilterValue
} from "../../components/admin/AdminFiltersModal";
import { resolveDateRange } from "../../utils/dateRangePresets";
import { useAuth } from "../../hooks/useAuth";
import { canViewAgencies } from "../../utils/agencyAccess";

// Card 53596: "A, B and C" rather than "A and B and C" when more than one
// filter's option source failed.
const describeFilterList = (labels) =>
  labels.length <= 1
    ? labels.join("")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

// Card 53596: a filter whose options failed to load is dropped ENTIRELY by
// AdminFiltersModal, so the absence needs a voice HERE, beside the Filters
// button. Deliberately a proportionate inline row and NOT the block-level
// <ListLoadError> panel: that component is for list REGIONS, and this page
// already uses it correctly for the welcome-call queue below.
const optionsErrorStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.375rem 0.625rem",
  border: "0.5px solid var(--color-tan-400)",
  borderRadius: "8px",
  background: "var(--color-tan-50)",
  color: "var(--color-error)",
  fontSize: "0.75rem",
  fontWeight: 600
};

const optionsRetryStyle = {
  padding: "0.25rem 0.625rem",
  border: "0.5px solid var(--color-tan-400)",
  borderRadius: "6px",
  background: "var(--color-cream)",
  color: "var(--color-charcoal)",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "background 0.2s ease, border-color 0.2s ease"
};

const CALLED_STATUSES = [
  { value: "not_called", label: "Not Called", color: "#dc2626", bg: "#dc26261a" },
  { value: "called", label: "Called", color: "#059669", bg: "#0596691a" },
  { value: "needs_follow_up", label: "Needs Follow-Up", color: "#f59e0b", bg: "#f59e0b1a" },
  { value: "unable_to_reach", label: "Unable to Reach", color: "#6b7280", bg: "#6b72801a" },
];

const INTERACTION_TYPES = [
  { value: "welcome_call_completed", label: "Welcome Call Completed", color: "#059669" },
  { value: "voicemail", label: "Voicemail", color: "#6366f1" },
  { value: "no_answer", label: "No Answer", color: "#dc2626" },
  { value: "callback_requested", label: "Callback Requested", color: "#f59e0b" },
  { value: "email_sent", label: "Email Sent", color: "#3b82f6" },
  { value: "sms_sent", label: "SMS Sent", color: "#8b5cf6" },
  // "general" (not "general_note") — the member_interactions check constraint rejects general_note
  { value: "general", label: "General Note", color: "#6b7280" },
];

// URL query params owned by the Filters experience — the URL is the source
// of truth, so refresh/back/forward and profile round-trips restore state
const SELECT_FILTER_PARAMS = ["status", "tier", "customTier", "agency"];

const readFiltersFromParams = (searchParams) => {
  const values = {};
  SELECT_FILTER_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) values[key] = value;
  });
  const preset = searchParams.get("preset");
  if (preset) {
    values.firstLogin = {
      preset: preset.toUpperCase(),
      start: searchParams.get("from") || "",
      end: searchParams.get("to") || ""
    };
  }
  return values;
};

const writeFiltersToParams = (values) => {
  const params = new URLSearchParams();
  SELECT_FILTER_PARAMS.forEach((key) => {
    if (values[key]) params.set(key, values[key]);
  });
  const firstLogin = values.firstLogin;
  if (firstLogin?.preset) {
    // an incomplete custom range applies nothing — don't persist it
    const isComplete = firstLogin.preset !== "CUSTOM" || (firstLogin.start && firstLogin.end);
    if (isComplete) {
      params.set("preset", firstLogin.preset);
      if (firstLogin.preset === "CUSTOM") {
        params.set("from", firstLogin.start);
        params.set("to", firstLogin.end);
      }
    }
  }
  return params;
};

function getStatusInfo(status) {
  return CALLED_STATUSES.find((s) => s.value === status) || CALLED_STATUSES[0];
}
function getInteractionLabel(type) {
  return INTERACTION_TYPES.find((t) => t.value === type)?.label || type;
}
function getInteractionColor(type) {
  return INTERACTION_TYPES.find((t) => t.value === type)?.color || "#6b7280";
}
function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}
function formatDateTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZone: "America/New_York"
  }) + " EST";
}

export default function WelcomeCallsPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ totalFirstLogins: 0, calledCount: 0, notCalledCount: 0, todayCount: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [standardTiers, setStandardTiers] = useState([]);
  const [customTiers, setCustomTiers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  // Card 53596: labels of the option sources whose load genuinely FAILED
  // (a permission 403 is not a failure -- see loadFilterOptions below).
  const [optionErrors, setOptionErrors] = useState([]);
  const [loadError, setLoadError] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [interactionsError, setInteractionsError] = useState(false);
  const [interactionType, setInteractionType] = useState("welcome_call_completed");
  const [interactionNotes, setInteractionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [statusDropdown, setStatusDropdown] = useState(null);

  const debounceRef = useRef(null);

  const filters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);

  const loadStats = useCallback(async () => {
    try {
      const data = await api.get("/admin/welcome-calls/stats");
      setStats(data);
    } catch {
      /* stats are non-critical — keep the previous values */
    }
  }, []);

  const loadMembers = useCallback(async (p = 1, s = search, f = filters) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (f.status) params.append("calledFilter", f.status);
      if (f.tier) params.append("membershipTier", f.tier);
      if (f.customTier) params.append("customTierId", f.customTier);
      if (f.agency) params.append("agencyId", f.agency);
      const range = resolveDateRange(f.firstLogin);
      if (range) {
        params.append("dateFrom", range.from);
        params.append("dateTo", range.to);
      }
      if (s) params.append("search", s);
      const data = await api.get(`/admin/welcome-calls/queue?${params}`);
      setMembers(data.members || []);
      setTotalPages(data.totalPages || 1);
      setPage(p);
    } catch (err) {
      // A failed queue load must NOT render the "no first-time logins" empty state:
      // that copy is an ANSWER, and answering "nobody to call" when we simply do not
      // know is how a broken backend silently stops the welcome-call workflow.
      console.error("Failed to load welcome-call queue:", err);
      setMembers([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  const loadInteractions = useCallback(async (memberId) => {
    setInteractionsLoading(true);
    setInteractionsError(false);
    try {
      const data = await api.get(`/admin/welcome-calls/${memberId}/interactions`);
      setInteractions(data.interactions || []);
    } catch (err) {
      // "No interactions yet" on a FAILED fetch tells the admin a member who was
      // already called has never been contacted -- which invites a duplicate call.
      console.error("Failed to load welcome-call interactions:", err);
      setInteractions([]);
      setInteractionsError(true);
    } finally {
      setInteractionsLoading(false);
    }
  }, []);

  const { hasPermission } = useAuth();

  // Card 53596: a select with ZERO options is SKIPPED outright by
  // AdminFiltersModal (its :264 predicate keeps a filter only when
  // `(def.options?.length ?? 0) > 0`), so a failed options load does not render
  // an empty dropdown -- the whole labelled control DISAPPEARS. An operator then
  // reads "could not load" as "this filter does not exist here", and there is no
  // empty-state string anywhere to contradict them.
  //
  // A PREDICTED 403 is NOT a load failure: these endpoints are gated by other
  // permission modules, so a denial keeps the original fail-soft silence
  // (card 49621). Only a genuine failure earns a voice. `error.status` is set on
  // every api.js error (services/api.js:255), which is what separates the arms.
  const loadFilterOptions = useCallback(() => {
    setOptionErrors([]);
    const noteFailure = (label, error) => {
      if (error?.status === 403) return;
      setOptionErrors((prev) => (prev.includes(label) ? prev : [...prev, label]));
    };

    // Gated on the permission the API already enforces (card 49621): without
    // agencies.view this request can only ever 403, and the old catch reported
    // that denial as "no agencies / API unavailable", which is false on both counts.
    if (canViewAgencies(hasPermission)) {
      api.get("/admin/agencies")
        .then((response) => setAgencies(response.agencies || []))
        .catch((error) => {
          setAgencies([]);
          noteFailure("Entity / Agency", error);
        });
    } else {
      setAgencies([]);
    }

    api.get("/admin/custom-membership-tiers")
      .then((response) => setCustomTiers((response.tiers || []).filter((t) => t.isActive)))
      .catch((error) => {
        setCustomTiers([]);
        noteFailure("Custom tier", error);
      });

    api.get("/admin/membership-tiers")
      .then((response) => setStandardTiers((response.tiers || []).filter((t) => t.isActive)))
      .catch((error) => {
        setStandardTiers([]);
        noteFailure("Membership tier", error);
      });
  }, [hasPermission]);

  useEffect(() => {
    loadStats();
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL is the source of truth for filters — this also runs the initial load
  // and resets pagination to page 1 whenever filters change
  useEffect(() => {
    loadMembers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filterDefs = useMemo(
    () => [
      // "First login" IS the queue's primary date (users.first_login_at) — labeled explicitly
      { key: "firstLogin", label: "First login", type: "dateRange" },
      {
        key: "status",
        label: "Call status",
        type: "select",
        placeholder: "All statuses",
        options: CALLED_STATUSES.map((s) => ({ value: s.value, label: s.label }))
      },
      {
        key: "tier",
        label: "Membership tier",
        type: "select",
        placeholder: "All tiers",
        options: standardTiers.map((tier) => ({ value: tier.name, label: tier.name }))
      },
      {
        key: "customTier",
        label: "Custom tier",
        type: "select",
        placeholder: "All custom tiers",
        options: customTiers.map((tier) => ({ value: tier.id, label: tier.name }))
      },
      {
        key: "agency",
        label: "Entity / Agency",
        type: "select",
        placeholder: "All agencies",
        options: agencies.map((agency) => ({ value: agency.id || agency._id, label: agency.name }))
      }
    ],
    [standardTiers, customTiers, agencies]
  );

  const activeFilterCount = countActiveFilters(filters);
  const filterChips = useMemo(() => buildFilterChips(filterDefs, filters), [filterDefs, filters]);

  const applyFilters = (nextValues) => {
    setSearchParams(writeFiltersToParams(nextValues));
    setFiltersOpen(false);
  };

  const handleRemoveChip = (chip) => {
    applyFilters(removeFilterValue(filters, chip));
  };

  const handleResetFilters = () => {
    applyFilters({});
  };

  const handleSearchInput = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadMembers(1, value), 300);
  };

  const openMember = (member) => {
    setSelectedMember(member);
    setInteractionType("welcome_call_completed");
    setInteractionNotes("");
    loadInteractions(member.id);
  };

  const handleSubmitInteraction = () => {
    if (interactionType === "welcome_call_completed") {
      setConfirmModal(true);
    } else {
      submitInteraction();
    }
  };

  const submitInteraction = async () => {
    if (!selectedMember) return;
    setSubmitting(true);
    setConfirmModal(false);
    try {
      await api.post(`/admin/welcome-calls/${selectedMember.id}/interactions`, {
        type: interactionType,
        notes: interactionNotes,
      });
      setInteractionNotes("");
      loadInteractions(selectedMember.id);
      loadMembers(page, search);
      loadStats();
      if (interactionType === "welcome_call_completed") {
        setSelectedMember((prev) => prev ? { ...prev, calledStatus: "called" } : null);
      }
    } catch (err) {
      alert("Failed to log interaction: " + (err.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  };

  const updateCalledStatus = async (memberId, newStatus) => {
    try {
      await api.patch(`/admin/welcome-calls/${memberId}/status`, { calledStatus: newStatus });
      loadMembers(page, search);
      loadStats();
      if (selectedMember && selectedMember.id === memberId) {
        setSelectedMember(prev => prev ? { ...prev, calledStatus: newStatus } : null);
      }
      setStatusDropdown(null);
    } catch (err) {
      alert("Failed to update status: " + (err.message || "Unknown error"));
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Welcome Calls</h1>
          <p>Track and manage first-time member outreach</p>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#f973161a", color: "#f97316" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div className={styles.statValue}>{stats.totalFirstLogins}</div>
          <div className={styles.statLabel}>Total First Logins</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#0596691a", color: "#059669" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div className={styles.statValue}>{stats.calledCount}</div>
          <div className={styles.statLabel}>Called</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#dc26261a", color: "#dc2626" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
          </div>
          <div className={styles.statValue}>{stats.notCalledCount}</div>
          <div className={styles.statLabel}>Not Called</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#3b82f61a", color: "#3b82f6" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div className={styles.statValue}>{stats.todayCount}</div>
          <div className={styles.statLabel}>Today&apos;s Logins</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className={styles.filters}>
        <div className={styles.filterRow}>
          <div className={styles.searchWrapper}>
            <Input placeholder="Search by name, email, or phone..." value={search} onChange={(e) => handleSearchInput(e.target.value)} />
          </div>
          <FiltersButton activeCount={activeFilterCount} onClick={() => setFiltersOpen(true)} />

          {optionErrors.length > 0 && (
            <div style={optionsErrorStyle} role="alert">
              <span>
                {`${describeFilterList(optionErrors)} ${
                  optionErrors.length > 1 ? "filters" : "filter"
                } could not be loaded`}
              </span>
              <button type="button" style={optionsRetryStyle} onClick={loadFilterOptions}>
                Retry
              </button>
            </div>
          )}
        </div>
        <FilterChips chips={filterChips} onRemove={handleRemoveChip} />
      </Card>

      <AdminFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filterDefs}
        values={filters}
        onApply={applyFilters}
        onReset={handleResetFilters}
      />

      {/* Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : loadError ? (
          <ListLoadError
            message="Could not load the welcome-call queue"
            onRetry={() => loadMembers(page)}
          />
        ) : members.length === 0 ? (
          <div className={styles.empty}>No first-time logins found</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Member Name</th>
                  <th>Member ID</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>First Login</th>
                  <th>Status</th>
                  <th>Interactions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const statusInfo = getStatusInfo(m.calledStatus);
                  return (
                    <tr key={m.id} className={styles.tableRow}>
                      <td>
                        <div className={styles.memberInfo}>
                          <div className={styles.avatar}>{m.initial}</div>
                          <span className={styles.name}>{m.firstName} {m.lastName}</span>
                        </div>
                      </td>
                      <td className={styles.memberId}>{m.memberId || "---"}</td>
                      <td className={styles.phone}>{m.phone || "---"}</td>
                      <td className={styles.email}>{m.email}</td>
                      <td className={styles.dateCell}>{formatDateTime(m.firstLoginAt)}</td>
                      <td>
                        <div className={styles.statusCellWrapper}>
                          <span
                            className={styles.statusBadge}
                            style={{ background: statusInfo.bg, color: statusInfo.color, borderColor: statusInfo.color + "40", cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); setStatusDropdown(statusDropdown === m.id ? null : m.id); }}
                          >
                            <span className={styles.statusDot} style={{ background: statusInfo.color }} />
                            {statusInfo.label}
                          </span>
                          {statusDropdown === m.id && (
                            <div className={styles.statusDropdown}>
                              {CALLED_STATUSES.map(s => (
                                <button
                                  key={s.value}
                                  className={styles.statusOption}
                                  onClick={(e) => { e.stopPropagation(); updateCalledStatus(m.id, s.value); }}
                                >
                                  <span className={styles.statusDot} style={{ background: s.color }} />
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={styles.interactionCount}>{m.interactionCount || 0}</span>
                      </td>
                      <td>
                        <div className={styles.actionBtns}>
                          <Button size="small" variant="outline" onClick={() => openMember(m)}>Log</Button>
                          <Button size="small" variant="outline" onClick={() => navigate(`/admin/members/${m.id}`)}>Profile</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Button disabled={page <= 1} onClick={() => loadMembers(page - 1)} size="small">Previous</Button>
            <span>Page {page} of {totalPages}</span>
            <Button disabled={page >= totalPages} onClick={() => loadMembers(page + 1)} size="small">Next</Button>
          </div>
        )}
      </Card>

      {/* Interactions Panel */}
      {selectedMember && (
        <div className={styles.panelOverlay} onClick={() => { setSelectedMember(null); setStatusDropdown(null); }}>
          <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{selectedMember.firstName} {selectedMember.lastName}</h3>
                <p>{selectedMember.email} {selectedMember.phone ? " | " + selectedMember.phone : ""}</p>
              </div>
              <div className={styles.panelHeaderActions}>
                <Button size="small" variant="outline" onClick={() => navigate(`/admin/members/${selectedMember.id}`)}>View Profile</Button>
                <button className={styles.closeBtn} onClick={() => setSelectedMember(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            <div className={styles.panelStatus}>
              {(() => {
                const si = getStatusInfo(selectedMember.calledStatus);
                return (
                  <span className={styles.statusBadge} style={{ background: si.bg, color: si.color, borderColor: si.color + "40" }}>
                    <span className={styles.statusDot} style={{ background: si.color }} />
                    {si.label}
                  </span>
                );
              })()}
              {selectedMember.calledAt && <span className={styles.calledDate}>Called {formatRelativeTime(selectedMember.calledAt)}</span>}
              <div className={styles.quickActions}>
                {selectedMember.calledStatus !== "called" && (
                  <Button size="small" onClick={() => updateCalledStatus(selectedMember.id, "called")}>Mark as Called</Button>
                )}
              </div>
            </div>

            <div className={styles.panelBody}>
              <h4>Interaction History</h4>
              {interactionsLoading ? (
                <div className={styles.loading}>Loading...</div>
              ) : interactionsError ? (
                <ListLoadError
                  message="Could not load interaction history"
                  onRetry={() => loadInteractions(selectedMember.id)}
                />
              ) : interactions.length === 0 ? (
                <div className={styles.emptySmall}>No interactions yet</div>
              ) : (
                <div className={styles.interactionList}>
                  {interactions.map((i) => (
                    <div key={i.id} className={styles.interactionItem}>
                      <span className={styles.typeBadge} style={{ background: getInteractionColor(i.interaction_type) + "18", color: getInteractionColor(i.interaction_type), borderColor: getInteractionColor(i.interaction_type) + "40" }}>
                        {getInteractionLabel(i.interaction_type)}
                      </span>
                      {i.notes && <p className={styles.interactionNotes}>{i.notes}</p>}
                      <div className={styles.interactionMeta}>
                        <span>{i.created_by_admin_name}</span>
                        <span>{formatDateTime(i.created_at)}</span>
                        <span className={styles.relTime}>{formatRelativeTime(i.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.panelForm}>
              <h4>Log Interaction</h4>
              <select className={styles.formSelect} value={interactionType} onChange={(e) => setInteractionType(e.target.value)}>
                {INTERACTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <textarea className={styles.formTextarea} placeholder="Add notes..." value={interactionNotes} onChange={(e) => setInteractionNotes(e.target.value)} rows={3} />
              <Button onClick={handleSubmitInteraction} disabled={submitting} fullWidth>
                {submitting ? "Saving..." : "Log Interaction"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <Modal isOpen={confirmModal} onClose={() => setConfirmModal(false)} title="Mark as Called?" size="small">
          <div className={styles.confirmContent}>
            <p>This will mark <strong>{selectedMember?.firstName} {selectedMember?.lastName}</strong> as Called and log a Welcome Call Completed interaction.</p>
            <div className={styles.confirmActions}>
              <Button variant="outline" onClick={() => setConfirmModal(false)}>Cancel</Button>
              <Button onClick={submitInteraction} disabled={submitting}>{submitting ? "Saving..." : "Confirm"}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
